import {
  INotificationSettings,
  INotificationSettingsForm,
  INotificationSettingsSkeleton,
  INotificationsSettingsTable,
} from './types';
import {
  AdminNotificationDestinationType,
  AdminNotificationDestinationType as Destination,
  AdminNotificationEventKey as NotificationEvent,
  IAdminNotificationInput,
  IAdminNotificationSettingsInput,
  IAdminNotificationSettingsQuery,
  IAdminNotificationSettingsSetMutation,
} from '../../../utils/apollo/graphqlSchema';
import { assertNotNil, Entries } from '../../../utils/types';
import { isEmpty } from 'lodash';
import { freeze } from '@reduxjs/toolkit';
import { toSlackDestinationInput } from '../../../components/Slack/utils';

const ALL_DESTINATIONS = freeze<AdminNotificationDestinationType[]>(
  [Destination.Email, Destination.Slack],
  true,
);

export const INITIAL_VALUES: INotificationSettingsForm =
  freeze<INotificationSettingsForm>(
    {
      emails: [],
      slackDestination: null,
      notifications: Object.values(NotificationEvent).reduce(
        (acc, key) => {
          acc[key] = {
            destinations: {
              [Destination.Email]: false,
              [Destination.Slack]: false,
            },
            eventDescription: '',
          };
          return acc;
        },
        {} as Record<NotificationEvent, INotificationSettings>,
      ),
    },
    true,
  );

export const NOTIFICATIONS_SETTINGS_SKELETON: INotificationSettingsSkeleton[] =
  freeze<INotificationSettingsSkeleton[]>(
    [
      {
        events: [
          {
            event: NotificationEvent.Schema,
            allowed: ALL_DESTINATIONS,
          },
          {
            event: NotificationEvent.SchemaConnectionPassword,
            allowed: ALL_DESTINATIONS,
          },
        ],
        header: 'Schema connections',
      },
      {
        events: [
          {
            event: NotificationEvent.User,
            allowed: ALL_DESTINATIONS,
          },
          {
            event: NotificationEvent.UserGroup,
            allowed: ALL_DESTINATIONS,
          },
        ],
        header: 'User & Group Management',
      },
      {
        events: [
          {
            event: NotificationEvent.Measure,
            allowed: ALL_DESTINATIONS,
          },
          {
            event: NotificationEvent.VirtualEvent,
            allowed: ALL_DESTINATIONS,
          },
          {
            event: NotificationEvent.BracketCondition,
            allowed: ALL_DESTINATIONS,
          },
          {
            event: NotificationEvent.BucketDef,
            allowed: ALL_DESTINATIONS,
          },
          {
            event: NotificationEvent.Cohort,
            allowed: ALL_DESTINATIONS,
          },
        ],
        header: 'Entity Updates',
      },
      {
        events: [
          {
            event: NotificationEvent.Incident,
            allowed: ALL_DESTINATIONS,
          },
        ],
        header: 'Incidents',
      },
      {
        events: [
          {
            event: NotificationEvent.ReportSettings,
            allowed: ALL_DESTINATIONS,
          },
          {
            event: NotificationEvent.FormatSettings,
            allowed: ALL_DESTINATIONS,
          },
          {
            event: NotificationEvent.AllowedDomainsSettings,
            allowed: ALL_DESTINATIONS,
          },
        ],
        header: 'Admin Settings',
      },
    ],
    true,
  );

export const toForm = (
  data:
    | IAdminNotificationSettingsQuery['adminNotificationSettings']
    | IAdminNotificationSettingsSetMutation['adminNotificationSettingsSet']['entity'],
  initialValues: INotificationSettingsForm,
): INotificationSettingsForm => {
  assertNotNil(data);
  const loadedData: INotificationSettingsForm = {
    emails: data.emails || [],
    slackDestination: data.slackDestination,
    notifications: { ...initialValues.notifications },
  };
  data.notifications.forEach((n) => {
    loadedData.notifications[n.event.key] = {
      destinations: {
        [Destination.Email]: n.destinationTypes.includes(Destination.Email),
        [Destination.Slack]: n.destinationTypes.includes(Destination.Slack),
      },
      eventDescription: n.event.name,
    };
  });
  return loadedData;
};

/**
 * Converts a notifications configuration object into an array of notification inputs for the admin system.
 *
 * @param {Record<AdminNotificationEventKey, INotificationSettings>} notifications - An object mapping
 * notification event keys to their respective settings
 * @returns {IAdminNotificationInput[]} An array of notification input objects, each containing:
 *   - event: The notification event key
 *   - destinationTypes: Array of enabled notification destinations (email, slack, etc.)
 *
 * @example
 * // Example usage:
 * const notificationsConfig = {
 *   ALLOWED_DOMAINS_SETTINGS: { EMAIL: true, SLACK: false },
 *   BRACKET_CONDITION: { EMAIL: true, SLACK: true }
 * };
 * const payload = toNotificationsPayload(notificationsConfig);
 * // Result: [
 * //   { event: 'ALLOWED_DOMAINS_SETTINGS', destinationTypes: ['EMAIL'] },
 * //   { event: 'BRACKET_CONDITION', destinationTypes: ['EMAIL', 'SLACK'] }
 * // ]
 */
const toNotificationsPayload = (
  notifications: Record<NotificationEvent, INotificationSettings>,
): IAdminNotificationInput[] => {
  return (
    (Object.entries(notifications) as Entries<typeof notifications>)
      .map(([k, v]) => ({
        event: k,
        destinationTypes: (
          Object.entries(v.destinations) as Entries<typeof v.destinations>
        )
          .filter(([_, checked]) => checked)
          .map(([d]) => d),
      }))
      // filter out the empty arrays.
      .filter((v) => v.destinationTypes.length)
  );
};

export const zipSkeletonWithForm = (
  form: INotificationSettingsForm,
): INotificationsSettingsTable[] =>
  NOTIFICATIONS_SETTINGS_SKELETON.map((sk) => ({
    header: sk.header,
    events: sk.events.map((e) => {
      const event = form.notifications[e.event];
      return {
        ...event,
        ...e,
      };
    }),
  }));

export const toPayload = (
  formData: INotificationSettingsForm,
): IAdminNotificationSettingsInput => {
  return {
    slackDestination: toSlackDestinationInput(formData.slackDestination),
    emails: [...(formData.emails || [])],
    notifications: !isEmpty(formData.notifications)
      ? toNotificationsPayload(formData.notifications)
      : [],
  };
};
